import { useState, useEffect, createContext, useContext, ReactNode } from 'react'

export interface AWSAccount {
  id: string
  name: string
  accountId: string
  region: string
  environment: 'production' | 'staging' | 'development'
  status: 'active' | 'inactive' | 'testing'
  description?: string
  createdAt: string
}

interface AWSAccountsContextType {
  accounts: AWSAccount[]
  addAccount: (account: Omit<AWSAccount, 'id' | 'createdAt'>) => void
  updateAccount: (id: string, account: Partial<AWSAccount>) => void
  deleteAccount: (id: string) => void
  getActiveAccounts: () => AWSAccount[]
}

const AWSAccountsContext = createContext<AWSAccountsContextType | undefined>(undefined)

// Mock initial data
const initialAccounts: AWSAccount[] = [
  {
    id: 'aws-001',
    name: 'danone-prod-east',
    accountId: '123456789012',
    region: 'us-east-1',
    environment: 'production',
    status: 'active',
    description: 'Production environment for ML inference',
    createdAt: '2025-01-10T10:30:00Z',
  },
  {
    id: 'aws-002',
    name: 'danone-staging-west',
    accountId: '234567890123',
    region: 'us-west-2',
    environment: 'staging',
    status: 'active',
    description: 'Staging environment for testing deployments',
    createdAt: '2025-01-08T14:20:00Z',
  },
  {
    id: 'aws-003',
    name: 'danone-dev-central',
    accountId: '345678901234',
    region: 'us-central-1',
    environment: 'development',
    status: 'active',
    description: 'Development environment for experiments',
    createdAt: '2025-01-12T09:15:00Z',
  },
  {
    id: 'aws-004',
    name: 'danone-analytics-eu',
    accountId: '456789012345',
    region: 'eu-west-1',
    environment: 'production',
    status: 'active',
    description: 'European analytics environment',
    createdAt: '2025-01-05T15:45:00Z',
  },
]

export function AWSAccountsProvider({ children }: { children: ReactNode }) {
  const [accounts, setAccounts] = useState<AWSAccount[]>(() => {
    // Try to load from localStorage first
    const saved = localStorage.getItem('aws-accounts')
    return saved ? JSON.parse(saved) : initialAccounts
  })

  // Save to localStorage whenever accounts change
  useEffect(() => {
    localStorage.setItem('aws-accounts', JSON.stringify(accounts))
  }, [accounts])

  const addAccount = (accountData: Omit<AWSAccount, 'id' | 'createdAt'>) => {
    const newAccount: AWSAccount = {
      ...accountData,
      id: `aws-${Date.now()}`,
      createdAt: new Date().toISOString(),
    }
    setAccounts((prev) => [...prev, newAccount])
  }

  const updateAccount = (id: string, updates: Partial<AWSAccount>) => {
    setAccounts((prev) =>
      prev.map((account) => (account.id === id ? { ...account, ...updates } : account))
    )
  }

  const deleteAccount = (id: string) => {
    setAccounts((prev) => prev.filter((account) => account.id !== id))
  }

  const getActiveAccounts = () => {
    return accounts.filter((account) => account.status === 'active')
  }

  const contextValue: AWSAccountsContextType = {
    accounts,
    addAccount,
    updateAccount,
    deleteAccount,
    getActiveAccounts,
  }

  return <AWSAccountsContext.Provider value={contextValue}>{children}</AWSAccountsContext.Provider>
}

export function useAWSAccounts() {
  const context = useContext(AWSAccountsContext)
  if (context === undefined) {
    throw new Error('useAWSAccounts must be used within an AWSAccountsProvider')
  }
  return context
}
